import { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, Clock, MapPin, Film, CalendarDays } from 'lucide-react';
import { demoEvents } from '../data/dummyData';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const typeColor = {
  shoot: 'bg-red-500/10 text-red-600 dark:text-red-400',
  meeting: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  review: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  deadline: 'bg-foreground/10 text-foreground',
};

export default function CalendarPage() {
  const [date, setDate] = useState(new Date(2026, 2, 1));

  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return demoEvents.filter(e => e.date === dateStr);
  };

  const allEvents = demoEvents.filter(e => {
    const d = new Date(e.date);
    return d.getMonth() === month && d.getFullYear() === year;
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  const prev = () => setDate(new Date(year, month - 1, 1));
  const next = () => setDate(new Date(year, month + 1, 1));

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="calendar-page">
      <motion.div variants={fadeUp} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">Shoots, deadlines, and milestones.</p>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <motion.div variants={fadeUp} className="lg:col-span-2">
          <div className="p-5 rounded-xl border border-border/50 bg-card">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prev} data-testid="prev-month-btn">
                <ChevronLeft className="w-4 h-4" strokeWidth={2} />
              </Button>
              <h2 className="font-heading text-base font-medium">{MONTHS[month]} {year}</h2>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={next} data-testid="next-month-btn">
                <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-px">
              {DAYS.map(d => (
                <div key={d} className="text-center text-[10px] text-muted-foreground py-2 font-medium">{d}</div>
              ))}
              {days.map((day, i) => {
                const events = getEventsForDay(day);
                const isToday = day && new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
                return (
                  <div key={day ? `day-${day}` : `empty-${i}`} className={`min-h-[80px] p-1.5 border border-border/20 rounded-lg ${day ? 'bg-background' : ''}`} data-testid={day ? `cal-day-${day}` : undefined}>
                    {day && (
                      <>
                        <span className={`text-xs font-medium ${isToday ? 'bg-foreground text-background w-6 h-6 rounded-full flex items-center justify-center' : 'text-muted-foreground'}`}>{day}</span>
                        {events.map(e => (
                          <div key={e.event_id} className={`mt-1 px-1.5 py-0.5 rounded text-[9px] truncate ${typeColor[e.event_type] || 'bg-secondary text-foreground'}`}>
                            {e.title.length > 20 ? e.title.slice(0, 18) + '...' : e.title}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Events List */}
        <motion.div variants={fadeUp}>
          <h2 className="font-heading text-lg font-medium mb-3">This Month</h2>
          <div className="space-y-2">
            {allEvents.length > 0 ? allEvents.map(e => (
              <div key={e.event_id} className="p-3.5 rounded-xl border border-border/50 bg-card" data-testid={`event-${e.event_id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className={`text-[9px] h-4 px-1 capitalize ${typeColor[e.event_type] || ''}`}>{e.event_type}</Badge>
                  <span className="text-[10px] text-muted-foreground">{e.date}</span>
                </div>
                <p className="text-sm font-medium">{e.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{e.description}</p>
                {e.time && <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={2} />{e.time}</p>}
              </div>
            )) : (
              <div className="text-center py-12">
                <CalendarDays className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" strokeWidth={2} />
                <p className="text-sm text-muted-foreground">No events this month</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}